import { useState } from 'react';
import {
  Copy,
  Inbox as InboxIcon,
  LogOut,
  Menu,
  ShieldCheck,
  Trash2,
  User as UserIcon,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../lib/api/client';
import { useInbox } from '../lib/api/inbox';
import { deleteAccount } from '../lib/auth/api';
import { disableGoogleAutoSelect } from '../lib/auth/google';
import { useAuthStore } from '../lib/auth/store';
import { useSidebarStore } from '../lib/sidebarStore';
import { cn } from '../lib/utils';
import { LocaleSwitcher } from './LocaleSwitcher';
import { NewsTicker } from './NewsTicker';
import { Badge } from './ui/badge';
import { useConfirm } from './ui/confirm';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useToast } from './ui/toast';

interface TopbarProps {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

export function Topbar({ title, subtitle, actions }: TopbarProps): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const toggleSidebar = useSidebarStore((s) => s.toggle);
  const { showToast } = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const [deleting, setDeleting] = useState(false);
  // Mobile-only inbox badge — desktop already shows it on the sidebar.
  // useInbox dedupes via React Query, so this and the sidebar share one fetch.
  const inboxQ = useInbox();
  const inboxCount = inboxQ.data?.totalCount ?? 0;

  function signOut(): void {
    disableGoogleAutoSelect();
    clear();
    navigate('/sign-in', { replace: true });
  }

  async function handleDeleteAccount(): Promise<void> {
    if (deleting) return;
    const ok = await confirm({
      title: 'Delete your account?',
      description:
        'This permanently deletes your account, bookmarks and reactions. This cannot be undone.',
      confirmLabel: 'Delete my account',
      variant: 'danger',
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await deleteAccount();
      showToast({ title: 'Account deleted', variant: 'success' });
    } catch (err) {
      // 401: the account is already gone (e.g. deleted from another
      // device) — fall through and sign out like a success.
      if (!(err instanceof ApiError && err.status === 401)) {
        showToast({ title: `Could not delete account: ${(err as Error).message}`, variant: 'danger' });
        setDeleting(false);
        return;
      }
    }
    disableGoogleAutoSelect();
    clear();
    navigate('/sign-in', { replace: true });
  }

  // Lets a correspondent paste their session JWT into the VON iPlayer
  // mobile app's manual sign-in (Go Live). It's the same token the
  // admin SPA authenticates with — copying it doesn't grant anything
  // new; the mobile app's `/live/*` calls are still server-validated
  // against the user's role.
  async function copyToken(): Promise<void> {
    if (!token) {
      showToast({ title: 'No active session token', variant: 'danger' });
      return;
    }
    try {
      await navigator.clipboard.writeText(token);
      showToast({ title: 'Token copied — paste it in VON iPlayer', variant: 'success' });
    } catch {
      showToast({ title: 'Clipboard not available', variant: 'danger' });
    }
  }

  return (
    <>
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-line bg-surface/85 px-4 backdrop-blur-md supports-[backdrop-filter]:bg-surface/70">
      <button
        type="button"
        onClick={toggleSidebar}
        className="rounded-md p-1.5 text-ink-subtle transition-colors hover:bg-surface-muted hover:text-ink md:hidden"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>
      <div className="flex flex-1 items-center gap-3">
        {title ? (
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle">
              VON · Admin
            </p>
            <h1 className="truncate font-heading text-[15px] font-semibold leading-tight text-ink">
              {title}
              {subtitle ? (
                <span className="ml-2 font-normal text-ink-subtle">— {subtitle}</span>
              ) : null}
            </h1>
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-1.5">
        {actions}
        {/* Mobile-only inbox badge. Desktop already surfaces this on the
         *  sidebar; rendering it here too would just duplicate the count. The
         *  `md:hidden` mirrors the hamburger menu's visibility on the left. */}
        <Link
          to="/inbox"
          aria-label={
            inboxCount > 0 ? `Inbox — ${inboxCount} items needing attention` : 'Inbox'
          }
          className="relative grid h-9 w-9 place-items-center rounded-md text-ink-subtle transition-colors hover:bg-surface-muted hover:text-ink md:hidden"
        >
          <InboxIcon className="h-5 w-5" />
          {inboxCount > 0 ? (
            <span
              aria-hidden
              className="absolute -right-0.5 -top-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand px-1 font-mono text-[10px] font-semibold tabular-nums leading-none text-white ring-2 ring-surface"
            >
              {inboxCount > 99 ? '99+' : inboxCount}
            </span>
          ) : null}
        </Link>
        <LocaleSwitcher />
        <span aria-hidden className="mx-1 hidden h-5 w-px bg-line sm:block" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="User menu"
              className="group flex items-center gap-2 rounded-full border border-line bg-surface py-1 pl-1 pr-3 transition-colors hover:border-line-strong hover:bg-surface-muted"
            >
              {user?.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt=""
                  className="h-7 w-7 rounded-full ring-1 ring-line"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="grid h-7 w-7 place-items-center rounded-full bg-brand-soft text-brand">
                  <UserIcon className="h-3.5 w-3.5" />
                </span>
              )}
              <span className="hidden flex-col items-start leading-none sm:flex">
                <span className="text-[12.5px] font-medium text-ink">
                  {user?.name?.split(' ')[0] ?? 'Account'}
                </span>
                <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-ink-subtle">
                  {user?.role ?? '—'}
                </span>
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[15rem]">
            <DropdownMenuLabel>
              <div className="space-y-1">
                <p className="text-sm font-medium text-ink">{user?.name ?? 'Account'}</p>
                <p className="truncate text-xs font-normal normal-case tracking-normal text-ink-muted">
                  {user?.email}
                </p>
                <div className="pt-1">
                  <Badge
                    variant={user?.role === 'admin' ? 'brand' : 'neutral'}
                    className={cn('uppercase tracking-[0.12em]')}
                  >
                    <ShieldCheck className="h-3 w-3" /> {user?.role}
                  </Badge>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => {
                // Keep the menu's selection from navigating focus before
                // the async clipboard write runs.
                e.preventDefault();
                void copyToken();
              }}
            >
              <Copy className="h-4 w-4" /> Copy app token
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={signOut} className="text-danger">
              <LogOut className="h-4 w-4" /> Sign out
            </DropdownMenuItem>
            {/* Staff accounts can't self-delete (the API refuses them — an
                administrator removes those), so only offer it to `user` roles. */}
            {user?.role === 'user' ? (
              <DropdownMenuItem
                disabled={deleting}
                onSelect={(e) => {
                  e.preventDefault();
                  void handleDeleteAccount();
                }}
                className="text-danger"
              >
                <Trash2 className="h-4 w-4" /> {deleting ? 'Deleting…' : 'Delete account'}
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
    {confirmDialog}
    {/* Sticks at `top-14` (= header height) so the two ride together as the
        page scrolls. Auto-hides for locales with no recent posts. */}
    <NewsTicker />
    </>
  );
}
